"use client";

import { useEffect, useRef, useState } from "react";
import { Track } from "@/data/playlist";

const DEFAULT_VOLUME = 0.2; // keep background music quiet by default

// Fisher–Yates shuffle for a fresh random order each session.
function shuffle<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function MusicPlayer() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const [open, setOpen] = useState(false);

  // Auto-discover the .ogg files in /public/music and shuffle them.
  useEffect(() => {
    fetch("/api/tracks")
      .then((r) => r.json())
      .then((list: Track[]) => setTracks(shuffle(list)))
      .catch(() => setTracks([]));
  }, []);

  const hasTracks = tracks.length > 0;
  const current = hasTracks ? tracks[index] : null;

  // Keep the audio element's volume in sync.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Hard-stop playback when this player unmounts. A detached <audio> element
  // keeps playing in most browsers until it's garbage-collected, so without
  // this an unmount (e.g. navigating to another route and back) would stack a
  // second, overlapping track on top of the first — once per navigation.
  useEffect(
    () => () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.src = "";
      }
    },
    [],
  );

  // Load + (optionally) play whenever the selected track changes, including
  // the initial track (music auto-plays by default). Browsers may block
  // autoplay before the user has interacted with the page, so fall back to
  // starting playback on the first click/keypress.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    audio.volume = volume;
    if (!playing) return;

    const tryPlay = () => {
      audio
        .play()
        .then(() => setPlaying(true))
        .catch(() => {});
    };

    audio.play().then(() => setPlaying(true)).catch(() => {
      setPlaying(false);
      window.addEventListener("pointerdown", tryPlay, { once: true });
      window.addEventListener("keydown", tryPlay, { once: true });
    });

    return () => {
      window.removeEventListener("pointerdown", tryPlay);
      window.removeEventListener("keydown", tryPlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const playTrack = (i: number) => {
    setIndex(i);
    setPlaying(true);
  };

  const next = () => {
    if (!hasTracks) return;
    setIndex((i) => (i + 1) % tracks.length);
    setPlaying(true);
  };

  const prev = () => {
    if (!hasTracks) return;
    setIndex((i) => (i - 1 + tracks.length) % tracks.length);
    setPlaying(true);
  };

  return (
    <div className="fixed bottom-0 left-0 z-40 w-full border-t border-white/10 bg-background/90 backdrop-blur">
      {/* Expandable track list */}
      {open && (
        <div className="max-h-48 overflow-y-auto border-b border-white/5 px-4 py-2">
          {hasTracks ? (
            <ul className="mx-auto max-w-3xl">
              {tracks.map((t, i) => (
                <li key={t.src}>
                  <button
                    onClick={() => playTrack(i)}
                    className={`w-full truncate rounded px-3 py-1.5 text-left text-sm transition hover:bg-white/5 ${
                      i === index ? "font-bold text-accent" : "text-white/70"
                    }`}
                  >
                    {i === index && playing ? "♪ " : ""}
                    {t.title}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mx-auto max-w-3xl py-2 text-center text-xs text-white/40">
              No tracks yet. Add .ogg files to{" "}
              <code className="text-accent">/public/music</code> and list them in{" "}
              <code className="text-accent">data/playlist.ts</code>.
            </p>
          )}
        </div>
      )}

      {/* Control bar — slimmer on phones so it eats less of the screen. */}
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-1.5 md:py-2">
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-white/50 transition hover:text-white"
          title="Playlist"
          aria-label="Toggle playlist"
        >
          ☰
        </button>

        <button
          onClick={prev}
          disabled={!hasTracks}
          className="text-lg text-white/70 transition hover:text-white disabled:opacity-30"
          aria-label="Previous track"
        >
          ⏮
        </button>
        <button
          onClick={togglePlay}
          disabled={!hasTracks}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-white transition hover:brightness-110 disabled:opacity-30 md:h-9 md:w-9"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? "❚❚" : "►"}
        </button>
        <button
          onClick={next}
          disabled={!hasTracks}
          className="text-lg text-white/70 transition hover:text-white disabled:opacity-30"
          aria-label="Next track"
        >
          ⏭
        </button>

        <div className="min-w-0 flex-1 truncate text-sm text-white/60">
          {current ? current.title : "No music loaded"}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40">🔊</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="h-1 w-20 cursor-pointer accent-accent"
            aria-label="Volume"
          />
        </div>
      </div>

      {current && (
        <audio
          ref={audioRef}
          src={current.src}
          onEnded={next}
          preload="none"
        />
      )}
    </div>
  );
}
